import { initializeApp } from 'firebase/app';
import {
  getFunctions,
  httpsCallable,
  connectFunctionsEmulator,
} from 'firebase/functions';
import {
  getAuth,
  connectAuthEmulator,
  setPersistence,
  browserSessionPersistence,
  signInAnonymously,
  reauthenticateWithCredential,
  EmailAuthProvider,
  signInWithEmailAndPassword,
} from 'firebase/auth';
import { getFirestore, connectFirestoreEmulator } from 'firebase/firestore';
import { getStorage, connectStorageEmulator } from 'firebase/storage';

// Make sure to have a valid .env file with valid
// Local environment variables if you plan to use npm run start
// And production environment variables for the firebase deploy command
const firebaseConfig = {};

let region = null;

if (process.env.NODE_ENV === 'development') {
  firebaseConfig.apiKey =
    process.env.REACT_APP_LOCAL_FIREBASE_API_KEY || 'default';
  firebaseConfig.authDomain =
    process.env.REACT_APP_LOCAL_FIREBASE_AUTH_DOMAIN || 'localhost';
  firebaseConfig.projectId =
    process.env.REACT_APP_LOCAL_FIREBASE_PROJECT_ID ||
    'dft-efm-electricvehicles-prod';
  region = process.env.REACT_APP_LOCAL_REGION || 'europe-west2';
} else {
  firebaseConfig.apiKey = process.env.REACT_APP_PRODUCTION_FIREBASE_API_KEY;
  firebaseConfig.authDomain =
    process.env.REACT_APP_PRODUCTION_FIREBASE_AUTH_DOMAIN;
  firebaseConfig.projectId =
    process.env.REACT_APP_PRODUCTION_FIREBASE_PROJECT_ID;
  firebaseConfig.storageBucket =
    process.env.REACT_APP_PRODUCTION_FIREBASE_STORAGE_BUCKET;
  firebaseConfig.messagingSenderId =
    process.env.REACT_APP_PRODUCTION_FIREBASE_MESSAGING_SENDER_ID;
  firebaseConfig.appId = process.env.REACT_APP_PRODUCTION_FIREBASE_APP_ID;

  region = process.env.REACT_APP_PRODUCTION_REGION;
}

export const app = initializeApp(firebaseConfig);

export const functions = getFunctions(app, region);
export const auth = getAuth(app);
export const firestore = getFirestore(app);
export const storage = getStorage();

setPersistence(auth, browserSessionPersistence);

// If the environment is development, connect to the emulators
// The backend project "evcg-backend" should run locally for the emulators to work
if (process.env.NODE_ENV === 'development') {
  connectFunctionsEmulator(functions, 'localhost', 5001);
  connectAuthEmulator(auth, 'http://localhost:9099');
  connectFirestoreEmulator(firestore, 'localhost', 8080);
  connectStorageEmulator(storage, 'localhost', 9199);
}
export const getWordingTemplates = httpsCallable(
  functions,
  'getWordingTemplates'
);
export const submitGrantApplication = httpsCallable(
  functions,
  'landlordApp-submitGrantApplication'
);
export const chooseAccountType = httpsCallable(
  functions,
  'landlordReg-chooseAccountType'
);
export const createAccount = httpsCallable(
  functions,
  'landlordReg-createAccount'
);
export const resendCode = httpsCallable(functions, 'landlordReg-resendCode');
export const checkEmail = httpsCallable(functions, 'landlordReg-checkEmail');
export const updateContactDetails = httpsCallable(
  functions,
  'landlordReg-updateContactDetails'
);
export const checkMobileNumber = httpsCallable(
  functions,
  'landlordReg-checkMobileNumber'
);
export const createPassword = httpsCallable(
  functions,
  'landlordReg-createPassword'
);
export const updateAccount = httpsCallable(
  functions,
  'landlordReg-updateAccount'
);
export const getCompanyData = httpsCallable(
  functions,
  'landlordReg-getCompanyData'
);
export const checkVATNumber = httpsCallable(
  functions,
  'landlordReg-checkVatNumber'
);
export const checkAnswers = httpsCallable(
  functions,
  'landlordReg-checkAnswers'
);
export const updateAccountSocialHousing = httpsCallable(
  functions,
  'landlordReg-updateAccountSocialHousing'
);

export const completeRegistration = httpsCallable(
  functions,
  'landlordReg-completeRegistration'
);

export const challangeInstallation = httpsCallable(
  functions,
  'landlordApp-challangeInstallation'
);

export const getGrantsData = httpsCallable(
  functions,
  'landlordApp-getGrantsData'
);
export const getConfigurableDeadlines = httpsCallable(
  functions,
  'getConfigurableDeadlines'
);
export const sendClaimToDynamicsQueue = httpsCallable(
  functions,
  'dashboard-sendClaimToDynamicsQueue'
);

export const updateStatus = httpsCallable(functions, 'dashboard-updateStatus');

export const getDashboardData = httpsCallable(functions, 'dashboard-getDbData');

export const getApplicationClaim = httpsCallable(
  functions,
  'dashboard-getApplicationClaim'
);

export const getUserData = httpsCallable(
  functions,
  'dashboard-userAccountDetails'
);

export const resetPasswordGeneral = httpsCallable(
  functions,
  'dashboard-resetPasswordGeneral'
);

export const resetPasswordInstaller = httpsCallable(
  functions,
  'installerJourney-resetPasswordInstaller'
);

export const checkStatus = httpsCallable(
  functions,
  'installerJourney-checkStatus'
);

export const submitChargepointSummaryData = httpsCallable(
  functions,
  'installerJourney-submitChargepointSummaryData'
);

export const addAdditionalChargepointModel = httpsCallable(
  functions,
  'installerJourney-addAdditionalChargepointModel'
);

export const saveChargepointModels = httpsCallable(
  functions,
  'installerJourney-saveChargepointModels'
);

export const submitClaim = httpsCallable(
  functions,
  'installerJourney-submitClaim'
);

export const changePassword = httpsCallable(functions, 'changePassword');

export const disableAccount = httpsCallable(functions, 'disableAccount');

export const reauthenticate = (newEmail, newPassword) => {
  const credential = EmailAuthProvider.credential(newEmail, newPassword);

  return reauthenticateWithCredential(auth.currentUser, credential);
};

export const signInRegistration = () => {
  return signInAnonymously(auth);
};

export const signIn = (email, password) => {
  return signInWithEmailAndPassword(auth, email, password);
};

export const getUser = () => {
  return auth.currentUser;
};

export const checkInstallerOZEVNumber = httpsCallable(
  functions,
  'landlordApp-checkInstallerOzevNumber'
);

export const confirmChargepointSockets = httpsCallable(
  functions,
  'landlordApp-confirmChargepointSockets'
);

export const checkSubcontractorOZEVNumber = httpsCallable(
  functions,
  'installerJourney-checkSubcontractorOzevNumber'
);
export const getAccount = httpsCallable(functions, 'dashboard-getAccount');

export const getCpManufacturers = httpsCallable(
  functions,
  'evhsInstaller-getCpManufacturers'
);

export const getCpModelsByManufacturer = httpsCallable(
  functions,
  'evhsInstaller-getCpModelsByManufacturer'
);

export const saveChargePoints = httpsCallable(
  functions,
  'installerJourney-saveChargePoints'
);

export const getTotalGrantAmount = httpsCallable(
  functions,
  'installerJourney-getTotalGrantAmount'
);

export const getChargePointDetails = httpsCallable(
  functions,
  'installerJourney-getChargePointDetails'
);

export const submitInvoiceData = httpsCallable(
  functions,
  'installerJourney-submitInvoiceData'
);

export const deleteChargePoint = httpsCallable(
  functions,
  'installerJourney-deleteChargepoint'
);

export const updateCpDetails = httpsCallable(
  functions,
  'installerJourney-updateCpDetails'
);

export const validateCpSerialNum = httpsCallable(
  functions,
  'installerJourney-validateCpSerialNum'
);

export const deletePhoto = httpsCallable(
  functions,
  'installerJourney-deletePhoto'
);

export const deleteInvoice = httpsCallable(
  functions,
  'installerJourney-deleteInvoice'
);

export const deleteSubcontractorDetails = httpsCallable(
  functions,
  'installerJourney-deleteSubcontractorDetails'
);

export const continueLater = httpsCallable(
  functions,
  'installerJourney-continueLater'
);

export const getUserAccountByDocId = httpsCallable(
  functions,
  'shared-getUserAccountByDocId'
);

export const completePMCCLientRegistration = httpsCallable(
  functions,
  'pmcClientReg-completeRegistration'
);

export const getDashboardPmcData = httpsCallable(
  functions,
  'dashboardPmc-getDbData'
);
export const updatePasswordChangeLink = httpsCallable(
  functions,
  'updatePasswordChangeLink'
);

export const updateCarparkClaimsCountersForUser = httpsCallable(
  functions,
  'carPark-updateCarparkClaimsCountersForUser'
);

export const getPMCClientData = httpsCallable(
  functions,
  'carPark-getPmcClientData'
);

export const getInstallerDetails = httpsCallable(
  functions,
  'evhsInstaller-getInstallerDetails'
);

export const dateCalculation = httpsCallable(
  functions,
  'shared-dateCalculation'
);
